"use client"

import type { Organization } from "better-auth/plugins/organization"
import { type ComponentProps, useContext } from "react"
import { useCurrentOrganization } from "../../hooks/use-current-organization"
import { AuthUIContext } from "../../lib/auth-ui-provider"
import { fileToBase64, resizeAndCropImage } from "../../lib/image-utils"
import { getLocalizedError } from "../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { Card } from "@papier/ui/components/ui/card"
import { SettingsImagePicker } from "@papier/ui/components/ui/settings-image-picker"
import { OrganizationLogo } from "./organization-logo"

export interface OrganizationLogoCardProps extends ComponentProps<typeof Card> {
    className?: string
    classNames?: SettingsCardClassNames
    localization?: AuthLocalization
    slug?: string
    onSuccess?: () => Promise<void>
}

export function OrganizationLogoCard(props: OrganizationLogoCardProps) {
    const { data: organization } = useCurrentOrganization({ slug: props.slug })
    return <OrganizationLogoForm {...props} organization={organization} />
}

function OrganizationLogoForm({
    className,
    classNames,
    localization: localizationProp,
    organization,
    onSuccess,
    slug: _slug,
    ...props
}: OrganizationLogoCardProps & { organization?: Organization | null }) {
    const {
        hooks: { useHasPermission, useListOrganizations },
        localization: authLocalization,
        organization: organizationOptions,
        mutators: { updateOrganization }
    } = useContext(AuthUIContext)
    const localization = { ...authLocalization, ...localizationProp }
    const { refetch: refetchOrganization } = useCurrentOrganization({
        slug: organization?.slug
    })
    const { refetch: refetchOrganizations } = useListOrganizations()
    const { data: hasPermission, isPending: permissionPending } = useHasPermission({
        organizationId: organization?.id,
        permissions: { organization: ["update"] }
    })
    const isPending = !organization || permissionPending

    const handleLogoChange = async (file: File) => {
        if (!organization || !organizationOptions?.logo || !hasPermission?.success) return
        const resizedFile = await resizeAndCropImage(
            file,
            crypto.randomUUID(),
            organizationOptions.logo.size,
            organizationOptions.logo.extension
        )
        const image = organizationOptions.logo.upload
            ? await organizationOptions.logo.upload(resizedFile)
            : await fileToBase64(resizedFile)
        if (!image) throw new Error(localization.IMAGE_UPDATE_ERROR)

        // Custom upload handlers persist the logo when completing the upload.
        if (!organizationOptions.logo.upload) {
            await updateOrganization({
                organizationId: organization.id,
                data: { logo: image }
            })
        }
        await refetchOrganization?.()
        await refetchOrganizations?.()
        await onSuccess?.()
    }

    const handleDeleteLogo = async () => {
        if (!organization || !hasPermission?.success) return
        if (organization.logo) {
            await organizationOptions?.logo?.delete?.(organization.logo)
        }
        await updateOrganization({
            organizationId: organization.id,
            data: { logo: "" }
        })
        await refetchOrganization?.()
        await refetchOrganizations?.()
        await onSuccess?.()
    }

    return (
        <Card className={cn("w-full text-start", className, classNames?.base)} {...props}>
            <SettingsImagePicker
                className="px-5 sm:px-6"
                title={localization.LOGO || "Logo"}
                description={localization.LOGO_DESCRIPTION || ""}
                hasImage={!!organization?.logo}
                isPending={isPending}
                disabled={!organizationOptions?.logo || !hasPermission?.success}
                readOnlyMessage={localization.LOGO_READ_ONLY}
                preview={
                    <OrganizationLogo
                        isPending={isPending}
                        className="size-16 rounded-xl"
                        classNames={{ ...classNames?.avatar, fallback: cn("rounded-xl", classNames?.avatar?.fallback) }}
                        organization={organization}
                        localization={localization}
                    />
                }
                labels={{
                    upload: localization.UPLOAD_IMAGE,
                    change: localization.CHANGE_IMAGE,
                    remove: localization.REMOVE_IMAGE,
                    instructions: localization.LOGO_INSTRUCTIONS,
                    uploading: localization.UPLOADING_IMAGE,
                    removing: localization.REMOVING_IMAGE,
                    updated: localization.IMAGE_UPDATED,
                    removed: localization.IMAGE_REMOVED,
                    invalidFile: localization.INVALID_IMAGE,
                    error: localization.IMAGE_UPDATE_ERROR
                }}
                getErrorMessage={(error) => {
                    const message: unknown = getLocalizedError({ error, localization })
                    return typeof message === "string" ? message : ""
                }}
                onUpload={handleLogoChange}
                onRemove={handleDeleteLogo}
            />
        </Card>
    )
}
